import { CSSProperties, FC } from 'react'

import { AspectRatio, Box, clsx, rem, Stack } from '@mantine/core'
import { useIntl } from 'react-intl'

import CheckSvg from 'components/icons/CheckSvg'
import FeatureCard from 'components/molecules/FeatureCard'
import { FeatureCardLayout } from 'components/molecules/FeatureCard/types'
import { useCommunity, useJoinCommunity } from 'hooks/useCommunity'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { basePath } from 'interfaces/constants'
import { CommunityItem } from 'interfaces/types/Community'

import { StyledHomeCommunityButton } from './HomeCommunity.styled'
import { CardCommunity } from '../CardCommunityV2'
import CarouselCard from '../CarouselCardV2/'

export type HomeCommunityData = {
  title: string
  message: string
  image: string
}

export type HomeCommunityProps = {
  isMobile?: boolean
  loading?: boolean
  data?: HomeCommunityData
  communities?: CommunityItem[]
  className?: string
  style?: CSSProperties
}

export const HomeCommunity: FC<HomeCommunityProps> = (props) => {
  const { loading: loadingProps, data, communities } = props
  const { formatMessage: f } = useIntl()
  const { communities: communitiesApi, loading } = useCommunity({ communities })
  const {
    loading: loadingCommunityJoin,
    communityJoined,
    onJoinCommunity,
  } = useJoinCommunity({
    communities: communitiesApi,
  })
  const currentDomain = useCurrentUrl()
  return (
    <FeatureCard
      layout={FeatureCardLayout.Horizontal}
      color="teal"
      url="/community/"
      styles={(theme) => ({
        [theme.fn.largerThan('sm')]: {
          paddingRight: 0,
        },
      })}
    >
      <FeatureCard.SectionName
        icon="/images/community-icon.png"
        data-event-category="Recommend Community"
        data-event-action="See All Click"
        data-event-label={`${currentDomain}${basePath}community`}
      >
        {f({ id: 'common.community', defaultMessage: 'Community' })}
      </FeatureCard.SectionName>
      <FeatureCard.Heading>{data?.title}</FeatureCard.Heading>
      <FeatureCard.ViewAll
        href="/community/"
        className="homeCommunityButton"
        data-event-category="Recommend Community"
        data-event-action="See All Click"
        data-event-label={`${currentDomain}${basePath}community`}
      >
        {f({
          id: 'home.community.buttonViewAll',
          defaultMessage: 'View all Communities',
        })}
      </FeatureCard.ViewAll>
      <FeatureCard.Content>
        <CarouselCard
          containScroll="keepSnaps"
          slideSize="auto"
          slideGap={16}
          align={'start'}
          autoplay
          styles={(theme) => ({
            viewport: {
              [theme.fn.smallerThan('sm')]: {
                paddingLeft: 16,
                paddingRight: 16,
              },
              [theme.fn.largerThan('sm')]: {
                paddingRight: 48,
              },
            },
          })}
        >
          <Stack spacing={24}>
            <CarouselCard.Footer
              sx={(theme) => ({
                [theme.fn.largerThan('sm')]: {
                  order: 2,
                },
              })}
            >
              {!!data?.message ? (
                <StyledHomeCommunityButton
                  radius="xl"
                  rightIcon={
                    !!data?.image && (
                      <AspectRatio ratio={47 / 32} style={{ width: rem(47) }}>
                        <img
                          src={data?.image}
                          className="doctor-ai"
                          loading="lazy"
                          alt=""
                        />
                      </AspectRatio>
                    )
                  }
                  className="homeCommunityButtonAI"
                  title={data?.message}
                >
                  <span>{data?.message}</span>
                </StyledHomeCommunityButton>
              ) : (
                <CarouselCard.Progress />
              )}
              <CarouselCard.Controls>
                <CarouselCard.Control
                  direction="prev"
                  data-event-category="Recommend Community"
                  data-event-action="See More Community"
                  data-event-label={`${currentDomain}${basePath}`}
                />
                <CarouselCard.Control
                  direction="next"
                  ata-event-category="Recommend Community"
                  data-event-action="See More Community"
                  data-event-label={`${currentDomain}${basePath}`}
                />
              </CarouselCard.Controls>
            </CarouselCard.Footer>
            <Box
              sx={(theme) => ({
                [theme.fn.smallerThan('sm')]: {
                  marginLeft: -16,
                  marginRight: -16,
                },
                [theme.fn.largerThan('sm')]: {
                  marginRight: -48,
                },
              })}
            >
              <CarouselCard.Body>
                {communitiesApi.map((community) => {
                  const {
                    id,
                    banner,
                    name,
                    slug,
                    joined: joinedProps,
                    thumbnail,
                    members_count,
                  } = community
                  const joined = communityJoined.includes(id) || joinedProps

                  if (!id) {
                    return null
                  }

                  return (
                    <CarouselCard.Slide key={String(id)}>
                      <CardCommunity
                        loading={loading || loadingProps}
                        item={{
                          id: 12,
                          permalink: `/community/${slug}`,
                          image:
                            banner?.[0]?.mobile_image ||
                            banner?.[0]?.image ||
                            thumbnail ||
                            '',
                          title: name || '',
                          members_count,
                        }}
                        itemTracking={{
                          image: {
                            'data-event-category': 'Recommend Community',
                            'data-event-action': 'Banners Click',
                            'data-event-label': `${currentDomain}${basePath}/community/${slug}`,
                          },
                          title: {
                            'data-event-category': 'Recommend Community',
                            'data-event-action': 'Title Click',
                            'data-event-label': `${currentDomain}${basePath}/community/${slug}`,
                          },
                        }}
                        memberText={f({
                          id: 'embedCommunity.card.members',
                          defaultMessage: 'Members',
                        })}
                        footerComponent={
                          <StyledHomeCommunityButton
                            radius="md"
                            variant="filled"
                            className={clsx('homeCommunityButtonJoin', {
                              joined,
                            })}
                            loading={!!loadingCommunityJoin[id]}
                            onClick={onJoinCommunity(community)}
                            leftIcon={joined ? <CheckSvg /> : undefined}
                            data-event-category="Recommend Community"
                            data-event-action={
                              joined ? 'Joined Community' : 'Join Community'
                            }
                            data-event-label={`${currentDomain}${basePath}/community/${slug}`}
                          >
                            {f({
                              id: joined
                                ? 'together.embed.topicCard.joined'
                                : 'together.embed.topicCard.joinNow',
                              defaultMessage: joined
                                ? 'You joined'
                                : 'Join now',
                            })}
                          </StyledHomeCommunityButton>
                        }
                      />
                    </CarouselCard.Slide>
                  )
                })}
              </CarouselCard.Body>
            </Box>
          </Stack>
        </CarouselCard>
      </FeatureCard.Content>
    </FeatureCard>
  )
}
